import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";
import type { CostRange } from "@/api/costs";

export interface ConcurrencyPoint { hour: string; maxConcurrent: number }
export interface ConcurrencySeries { userId: string; points: ConcurrencyPoint[] }
export interface ConcurrencySummary {
  contractVersion: 1;
  scope: "user" | "tenant";
  range: CostRange;
  bucket: "hour";
  series: ConcurrencySeries[];
  unattributedRunCount: number;
}

export class ConcurrencyApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = "ConcurrencyApiError";
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const nonnegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const dateString = (value: unknown): value is string =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

export function parseConcurrencySummary(value: unknown, scope: "user" | "tenant"): ConcurrencySummary {
  const invalid = () => new ConcurrencyApiError("Invalid concurrency response", 200, "concurrency_invalid_response");
  if (!isRecord(value) || value.contractVersion !== 1 || value.scope !== scope || value.bucket !== "hour" || !isRecord(value.range)) throw invalid();
  const range = value.range;
  if (range.timezone !== "UTC" || !dateString(range.startDate) || !dateString(range.endDateExclusive)
      || !nonnegativeInteger(range.days) || range.days < 1 || range.days > 90
      || new Date(`${range.startDate}T00:00:00Z`).getTime() + range.days * 86_400_000 !== new Date(`${range.endDateExclusive}T00:00:00Z`).getTime()
      || !nonnegativeInteger(value.unattributedRunCount) || !Array.isArray(value.series)) throw invalid();
  const start = Date.parse(`${range.startDate}T00:00:00Z`);
  const end = Date.parse(`${range.endDateExclusive}T00:00:00Z`);
  const seen = new Set<string>();
  const series = value.series.map((item): ConcurrencySeries => {
    if (!isRecord(item) || typeof item.userId !== "string" || !item.userId || seen.has(item.userId) || !Array.isArray(item.points)) throw invalid();
    seen.add(item.userId);
    let previous = -Infinity;
    const points = item.points.map((point): ConcurrencyPoint => {
      if (!isRecord(point) || typeof point.hour !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:00:00Z$/.test(point.hour)
          || !nonnegativeInteger(point.maxConcurrent)) throw invalid();
      const time = Date.parse(point.hour);
      if (!Number.isFinite(time) || time < start || time >= end || time <= previous) throw invalid();
      previous = time;
      return { hour: point.hour, maxConcurrent: point.maxConcurrent };
    });
    return { userId: item.userId, points };
  });
  if (scope === "user" && series.length > 1) throw invalid();
  return { contractVersion: 1, scope, range: range as unknown as CostRange, bucket: "hour", series, unattributedRunCount: value.unattributedRunCount as number };
}

async function fetchConcurrency(scope: "user" | "tenant", getToken: AccessTokenGetter, days: number): Promise<ConcurrencySummary> {
  const response = await fetchWithAuth(`/api/costs/concurrency/${scope === "user" ? "me" : "tenant"}?days=${encodeURIComponent(String(days))}`, { headers: {} }, getToken)
    .catch((error: unknown) => { throw new ConcurrencyApiError(error instanceof Error ? error.message : "Network error", 0, "concurrency_network_error"); });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const details = isRecord(body) ? body : {};
    throw new ConcurrencyApiError(typeof details.error === "string" ? details.error : `HTTP ${response.status}`, response.status, typeof details.code === "string" ? details.code : undefined);
  }
  return parseConcurrencySummary(body, scope);
}

export const fetchMyConcurrency = (getToken: AccessTokenGetter, days = 7) => fetchConcurrency("user", getToken, days);
export const fetchTenantConcurrency = (getToken: AccessTokenGetter, days = 7) => fetchConcurrency("tenant", getToken, days);
export const isConcurrencyUnsupportedError = (error: unknown) => error instanceof ConcurrencyApiError && (error.code === "concurrency_unsupported" || [404, 405, 501].includes(error.status));
export const isConcurrencyForbiddenError = (error: unknown) => error instanceof ConcurrencyApiError && error.status === 403;
